/**
 * The API surface the site reads.
 *
 * Types are declared here rather than generated, because the endpoints are shaped for these views
 * specifically. If they drift, the compiler should complain in one place.
 *
 * That last sentence is currently a hope rather than a mechanism, and it has already failed once.
 * `MatchView` and `OddsQuote` used to live at the top of this file, describing a per-match shape
 * with `odds: OddsQuote[]` and `side: 'radiant' | 'dire'`. The server stopped producing it when
 * odds moved onto the fixture and sides became A/B — and nothing broke, because nothing compares
 * these declarations against the server. They were dead on both sides at once: the `ToView` method
 * that produced the shape had no callers either. Both are now deleted.
 *
 * Closing the gap for real is item T2 of the backend plan: response DTOs on the server, an OpenAPI
 * document, and these types generated from it.
 */

export interface LeagueSummary {
  leagueId: number
  name: string | null
  tierRank: number
  isFeatured: boolean
  matchCount: number
  /**
   * When its first and last match were played. Null for a league we know of but hold no games for.
   *
   * From the matches rather than from a column, because OpenDota's league record has no start or
   * end. They exist for the fantasy season picker: a tournament is several league ids with nearly
   * identical names, and dates are what tell them apart.
   */
  firstMatch?: string | null
  lastMatch?: string | null
}

/**
 * What a map row is.
 *
 * `pending` and `void` both mean "no result", and keeping them apart is the point: a Bo3 decided
 * 2-0 never plays its third map, and every book refunds that stake rather than settling it as a
 * loss. The same distinction `FixtureSettlement` draws.
 */
/**
 * `awaiting` is played-but-unsettled and `pending` is not-yet-played, and they must stay apart.
 *
 * OpenDota indexes hours behind during a tournament, so a map that finished an hour ago routinely
 * has no result here yet. Rendering that as "Not started" beside a live game two is a card
 * contradicting itself, which is what the tournament page did on 2026-08-20.
 */
export type MapStatus = 'played' | 'live' | 'awaiting' | 'pending' | 'void'

/** One map within a series, expressed from team A's point of view. */
export interface MapView {
  /** Null until Valve opens the lobby, which is when a map becomes a match. */
  matchId: number | null
  startTime: number | null
  isLive: boolean
  gameNumber: number
  status: MapStatus
  /** Sides swap between maps, so this records which side team A took here. Null before the lobby. */
  teamAIsRadiant: boolean | null
  /** Null while unplayed. */
  teamAWon: boolean | null
  preDraftTeamA: number | null
  postDraftTeamA: number | null
  predictedTeamA: boolean | null
  predictionCorrect: boolean | null
}

/**
 * A series — the unit people follow.
 *
 * Team A is fixed by the first game and every probability is expressed for them, because a
 * radiant-relative number would flip meaning between maps as the sides swap.
 */
export interface SeriesView {
  seriesId: number | null
  leagueName: string | null
  startTime: number
  seriesType: number | null
  /** "Bo1" / "Bo3" / "Bo5". */
  seriesLabel: string
  teamA: string | null
  teamB: string | null
  /** Valve CDN badge URL. Null for the ~20% of teams with none — render initials instead. */
  teamALogo: string | null
  teamBLogo: string | null
  scoreA: number
  scoreB: number
  isLive: boolean
  decided: boolean
  /** Our estimate for the next single map. Null once the series is decided. */
  mapProbabilityTeamA: number | null
  /**
   * Chance of taking the series from the current score. Null once decided — at that point it is
   * 1 or 0, which restates the scoreline rather than forecasting anything.
   */
  seriesProbabilityTeamA: number | null
  /** What we made it before game one — the number comparable to a bookmaker's series price. */
  preSeriesProbabilityTeamA: number | null
  /** Whether that pre-series favourite went on to win. Null while the series is open. */
  seriesPredictionCorrect: boolean | null
  /**
   * What the market offered on this fixture and what we said about it. Null for the many series
   * nobody priced. Present on finished series too — that is the only place the outcome is known.
   */
  bet: SeriesBet[] | null
  /**
   * Markets the format offers that the book we quote never put a usable price on.
   *
   * Named rather than omitted: an absent row cannot be told apart from a market nobody priced,
   * and those are different facts. Pinnacle opens a Bo3's third map only once the series needs
   * one, so a card routinely lists Series, Map 1 and Map 2 and stops while other books have
   * priced Map 3.
   */
  unpricedMarkets: string[] | null
  maps: MapView[]
}

/** One market on a series we captured a price for, with our verdict and its outcome. */
export interface SeriesBet {
  /** "series" or "map1". */
  market: string
  bookmaker: string
  /** The team the suggestion is on, by name. */
  side: string | null
  odds: number
  ourProbability: number
  /** Ours minus what the price implies, in probability points. */
  edge: number
  /** Whether it cleared the minimum edge. A quote below it is shown but not advised. */
  recommended: boolean
  capturedAt: number
  /** The price was struck before the heroes were visible — which is every capture so far. */
  pricedBeforeDraft: boolean
  /** Null until settled, and also null when voided. */
  won: boolean | null
  /**
   * The series never reached this map, so the stake comes back. Neither a win nor a loss — a Bo3
   * decided 2-0 voids every map-3 bet.
   */
  voided: boolean
}

export interface LeagueHub {
  league: { leagueId: number; name: string | null; tierRank: number }
  /**
   * Series, time-ordered. Grouping into days happens in the browser, because only it knows the
   * reader's timezone — a server-side day boundary puts late matches on the wrong date.
   */
  series: SeriesView[]
  /** Individual maps called correctly. */
  accuracy: { correct: number; total: number; rate: number } | null
  /**
   * Series called correctly, from the pre-series estimate.
   *
   * Always the higher of the two and not comparable to it: a Bo3 gives the stronger side two
   * chances out of three, so the format magnifies a map edge into a bigger series edge on its own.
   */
  seriesAccuracy: { correct: number; total: number; rate: number } | null
  /**
   * The same maps, split by what was known when the call was made. Post-draft counts only
   * predictions that genuinely saw the ten heroes — a "post-draft" call made without them is the
   * pre-draft number relabelled, and counting it would compare the model against itself.
   */
  mapAccuracy: {
    preDraft: { correct: number; total: number; rate: number } | null
    postDraft: { correct: number; total: number; rate: number } | null
  }
}

/** One side of a market, with the price it would need to be worth taking. */
export interface BreakEvenSide {
  team: string | null
  probability: number
  /** 1/p — zero expected value. Not a good bet, just the boundary. */
  fairOdds: number | null
  /** The price that clears our minimum edge. Null when no price on this side would. */
  valueOdds: number | null
}

export interface BreakEven {
  available: boolean
  minimumEdge?: number
  map?: { teamA: BreakEvenSide; teamB: BreakEvenSide }
  /** Null for a Bo1, where the series and the map are the same bet. */
  series?: { teamA: BreakEvenSide; teamB: BreakEvenSide } | null
}


/** One side of a bookmaker's market, with our edge already worked out server-side. */
export interface MarketSide {
  team: string
  odds: number
  /** The first price we ever saw on this side. Equals `odds` until the line moves. */
  openingOdds: number
  /** What this price implies, margin and all. What a stake here is actually paid at. */
  impliedProbability: number
  /**
   * The same price with the margin removed: the book's opinion rather than its offer.
   *
   * Not what the edge is measured from, and not shown. It answers "do we disagree with this book",
   * which is a different question from "does this price pay" — and printing it as the edge put a
   * highlighted 1.31 beside a card saying the bet needed 1.36.
   */
  marketProbability: number
  /**
   * Ours minus what the price implies, in probability points. Null when we have no estimate.
   *
   * The same number the threshold is built from: `edge >= e` is exactly `odds >= 1/(p − e)`. Note
   * the two sides sum to `1 − overround` rather than to zero, so both can be negative — that is the
   * margin showing, and it is the honest picture of a market not worth betting into.
   */
  edge: number | null
}

/**
 * What our rules say about a market.
 *
 * Decided on the server, not here. The card used to hard-code `edge >= 0.03` to colour a figure,
 * which is a second copy of the betting rule living in a component — and the verdict word itself
 * only ever appeared once a match had STARTED, so it arrived after the market closed and was
 * withheld while a reader could still act.
 */
export interface MarketVerdict {
  /** The side our rules would take, or null when neither clears the bar. */
  side: 'A' | 'B' | null
  recommended: boolean
  edge: number
}

export interface MarketQuote {
  /** The canonical slug, which is what is stored and compared. */
  bookmaker: string
  /** The operator's own name, for printing. Never derive this in a component. */
  bookmakerName: string
  /** "series" or "map1". */
  market: string
  /** Both sides summed, e.g. 1.07 for a 7% book. */
  overround: number
  capturedAt: number
  /** How many times this market has been captured. */
  captures: number
  /**
   * Movement from the opening price, in decimal-odds points. Null until a market has been seen
   * twice — reporting "no movement" from one observation claims a stability never measured.
   */
  movement: { since: number; teamA: number; teamB: number } | null
  teamA: MarketSide
  teamB: MarketSide
  /** Null when we have no estimate for this market. */
  verdict: MarketVerdict | null
}

/**
 * A match that has not started yet.
 *
 * Distinct from {@link SeriesView} on purpose: this comes from the published schedule, not from
 * played games, so there are no maps, no score, and the lineup behind the probability is an
 * assumption rather than an observation. Merging the two types would lose exactly that distinction.
 */
export interface Fixture {
  id: number
  /** Unix seconds, UTC. */
  startTime: number
  teamAName: string
  teamBName: string
  teamALogo: string | null
  teamBLogo: string | null
  seriesType: number | null
  seriesLabel: string
  tournament: string | null
  leagueId: number | null
  modelVersion: string | null
  /**
   * How the lineup behind the prediction was arrived at: "high" (they played together this week),
   * "medium" (an older lineup), "low" (a declared roster nobody has seen play). Null when we could
   * not resolve a roster at all, in which case there is no probability either.
   */
  rosterConfidence: 'high' | 'medium' | 'low' | 'none' | null
  mapProbabilityTeamA: number | null
  seriesProbabilityTeamA: number | null
  /**
   * The draft-aware numbers. Null until all ten heroes are locked and we have seen them — never a
   * copy of the pre-draft figure, so a caller can rely on "present means the draft is known".
   */
  draftProbabilityTeamA: number | null
  draftSeriesProbabilityTeamA: number | null
  /**
   * The three observations that drive everything timing-related, in sequence.
   *
   * `lobbySeenAt` — Valve announced the lobby; the draft may still be running.
   * `draftSeenAt` — all ten heroes locked; the horn has not sounded.
   * `gameStartedAt` — the clock was seen running; every market on this map is shut.
   *
   * The published kick-off is none of these. It is a third party's estimate, and a draft settles
   * eleven to eighteen minutes after it.
   */
  lobbySeenAt: number | null
  draftSeenAt: number | null
  gameStartedAt: number | null
  draftModelVersion: string | null
  breakEven: BreakEven
  /** Live bookmaker prices, when any have been captured. Null when none exist. */
  markets: MarketQuote[] | null
}

export interface BetRow {
  /** True when the series never reached this map: stake returned, not lost. */
  voided?: boolean
  fixtureId: number
  startTime: number
  /** "series" or "map1" — the same fixture is priced separately in each. */
  market: string
  teamA: string
  teamB: string
  /** The team name, not a side code. Which team the suggestion is on. */
  side: string
  decimalOdds: number
  modelProbability: number
  /** What the price implies, margin and all. `edge` is `modelProbability` minus this. */
  impliedProbability: number
  /** The price with the margin stripped — the book's opinion. Kept for book-against-book checks. */
  marketProbability: number
  edge: number
  stakeFraction: number
  /** The raw edge rule's answer, before the sharp-book check. */
  shouldBet: boolean
  /** What is actually staked: the raw rule, minus anything the sharp book contradicts. */
  recommended: boolean
  /** Plain-language explanation, rendered verbatim — the reader should be able to disagree. */
  reason: string
  /**
   * What the sharp reference book makes of this price. Null when we hold no sharp line for the
   * market — silence, never a fabricated all-clear.
   */
  sharp: {
    bookmaker: string
    /** Its own name, for printing. */
    bookmakerName: string
    odds: number
    probability: number
    /** Sharp minus the book being bet. Positive means the sharp book agrees the price is long. */
    edge: number
    corroborated: boolean
  } | null
  /** Whether the fixture has been linked to the series it became. */
  resolved: boolean
  /** True while the series is being played — the price is gone, the row is history. */
  isLive: boolean
  /** Negative once it has started. Drives "in 40 min" on a row you can still act on. */
  startsInSeconds: number
  scoreA: number
  scoreB: number
  /** Null while the market is undecided — never conflate that with a loss. */
  won: boolean | null
  profit: number | null
  /**
   * Priced against the post-draft number, against a price struck before the draft.
   *
   * On a settled row that makes the edge a COUNTERFACTUAL: a bet is only real if the market was
   * still open between the heroes locking and the horn, and every capture so far was struck
   * pre-draft. The page says so rather than banking it.
   */
  counterfactual: boolean
  /** The de-vigged probability this side opened at. Null when we only ever saw the market once. */
  openingProbability?: number | null
  /**
   * How far the line travelled between opening and closing, in probability, for the side we picked.
   * Positive means the market came towards us.
   *
   * NOT the same as beating the closing line: a suggestion here is struck AT the closing price, so
   * that comparison is zero by construction. This is the weaker, measurable claim — did our number
   * anticipate where the market ended up.
   */
  lineMove?: number | null
}

export interface BetRecord {
  /** The canonical slug, which is what is stored and what a filter round-trips. */
  bookmaker: string
  /** The operator's own name, for printing. Never derive this in a component. */
  bookmakerName: string
  /** Every book we hold prices for, ordered by relevance, so a reader can pick their own. */
  availableBookmakers: { book: string; name: string; fixtures: number }[]
  /** The reference book suggestions are cross-checked against. */
  sharpBookmaker: string
  /** Its own name, for printing. */
  sharpBookmakerName: string
  /** False when betting into the reference book itself, where the cross-check cannot run. */
  sharpCheckActive: boolean
  minEdge: number
  summary: {
    considered: number
    placed: number
    /** Suggested but not yet settled. Deliberately separate from `placed`. */
    open: number
    wins: number
    hitRate: number | null
    stakedBankrolls: number
    profitBankrolls: number
    roi: number | null
    /**
     * Whether the market moved towards the sides we picked. Null when no market was ever captured
     * twice — which is not the same as the lines not having moved.
     */
    lineMove: { measured: number; withUs: number; meanPoints: number } | null
  }
  bets: BetRow[]
}


export interface ScoreSummary {
  count: number
  correct: number
  accuracy: number
  /** Mean squared error on the probability. Lower is better; 0.25 is the coin-flip baseline. */
  brier: number
  brierBaseline: number
  /** Punishes confident mistakes hardest — the behaviour that costs money. 0.693 is the baseline. */
  logLoss: number
  logLossBaseline: number
}

export interface CalibrationBin {
  from: number
  to: number
  count: number
  /** Mean probability we published in this bin. Null when the bin is empty. */
  predicted: number | null
  /** How often that actually happened. */
  actual: number | null
}

export interface ModelCard {
  modelVersion: string
  exportedAt: string
  /** Nothing after this was seen during training — the honest part of any backtest claim. */
  trainedThrough: string
  featureCount: number
  features: string[]
  ratedPlayers: number
  ratingsAsOf: string
  calibration: {
    kind: string
    intercept: number
    coefLogit: number
    coefLogitField: number
  }
}

/**
 * One trained model, and what it is allowed to be judged on.
 *
 * `trainedThrough` is the load-bearing field: nothing at or before it counts toward this model's
 * record, because it was in the training data. `excludedInSample` is how many of its predictions
 * that rule removed — stated rather than quietly subtracted, since the first version of this site
 * published 108 such fits as an 83% prediction record.
 */
export interface ModelHistoryEntry {
  version: string
  name: string | null
  byline: string | null
  trainedThrough: number
  exportedAt: number
  activatedAt: number | null
  retiredAt: number | null
  isCurrent: boolean
  /** 'champion' publishes; 'shadow' scores the same matches silently; null once retired. */
  role: 'champion' | 'shadow' | null
  featureCount: number
  trainingMatches: number
  heads: string | null
  provenance: string | null
  stats: ScoreSummary | null
  excludedInSample: number
}

/**
 * The live models measured on the matches they all scored.
 *
 * Not the same as reading their records side by side: each model is barred from being judged on
 * matches it trained on, so their records cover different matches. `basis` is how many
 * (point, match) pairs they have in common — the sample the comparison actually rests on.
 */
export interface HeadToHead {
  basis: number
  models: { version: string; role: 'champion' | 'shadow'; stats: ScoreSummary | null }[]
}

export interface Stats {
  overall: ScoreSummary | null
  byPoint: { point: string; stats: ScoreSummary }[]
  byVersion: { version: string; stats: ScoreSummary | null }[]
  /** Null until two models are live with matches in common. */
  headToHead: HeadToHead | null
  models: ModelHistoryEntry[]
  excludedInSample: number
  calibration: CalibrationBin[]
  byConfidence: { band: string; from: number; to: number; stats: ScoreSummary | null }[]
}


/**
 * Our published probabilities against a bookmaker's, on the same events, with the results.
 *
 * The site's central claim, measured. Every other figure grades the model against what happened;
 * this one grades it against the only forecaster worth beating. The theory, and the five rules that
 * decide what may be paired at all, are in `docs/MARKET-BENCHMARK.md`.
 */
export interface MarketBenchmark {
  bookmaker: string
  bookmakerName: string
  availableBookmakers: { book: string; name: string; fixtures: number }[]
  /** 'pre_draft' is the honest comparison; 'post_draft' is a counterfactual. */
  point: 'pre_draft' | 'post_draft'
  /** True in post-draft mode: our number saw the heroes and the price mostly did not. */
  counterfactual: boolean
  /** Which assumption removed the bookmaker's margin. */
  devigMethod: string
  basis: BenchmarkBasis
  /** The headline. A map is what the model actually estimates. Null when nothing paired. */
  maps: BenchmarkSlice | null
  /** The same book on the series market, where our number is a conversion of the map one. */
  series: BenchmarkSlice | null
  rows: BenchmarkRow[]
}

/**
 * What the comparison rests on, and everything it had to leave out.
 *
 * The exclusions are published rather than quietly subtracted. A reader comparing this page against
 * the raw count of captured prices should be able to see where the difference went — which is the
 * lesson from a version of this site that published 108 fits to training data as a prediction record.
 */
export interface BenchmarkBasis {
  /** Fixtures with at least one paired forecast. The sample size every interval rests on. */
  fixtures: number
  /** Markets across them. Larger, and not what the intervals rest on. */
  observations: number
  from: number | null
  to: number | null
  /** Usually more than one: the model changing mid-record is part of the record. */
  modelVersions: { version: string; count: number }[]
  noPrice: number
  notSettled: number
  /** Maps the series never reached. Refunded, so there is no outcome to grade either side on. */
  void: number
  noForecast: number
  /** Dropped because the match was inside that model's own training window. */
  inSample: number
}

/** One forecaster's scores on one set of events. */
export interface ForecasterScores {
  count: number
  brier: number
  logLoss: number
  /** Not a proper scoring rule, so it ranks nothing. Reported because a reader asks for it. */
  accuracy: number
  /** Mean distance from evens. A forecaster with less information must sit closer to 50%. */
  dispersion: number
}

/**
 * A paired difference in scores, clustered on the fixture.
 *
 * `mean` is market minus model, so **positive means our number scored better**. `clusters` is the
 * sample size the interval actually rests on: a best-of-three contributes up to four markets that
 * are nearly the same observation, and counting them separately would halve the standard error for
 * free.
 */
export interface PairedDifference {
  mean: number
  standardError: number
  low: number
  high: number
  pValue: number
  clusters: number
  observations: number
  /** The smallest true gap this many fixtures could have found, at 80% power. */
  detectableDifference: number
}

/** Uncertainty − resolution + reliability = Brier, exactly. */
export interface MurphyTerms {
  /** The events' own variance. Identical for both forecasters by construction. */
  uncertainty: number
  /** Information. The term this site is known to be short of, and the one no recalibration moves. */
  resolution: number
  /** Miscalibration. What repairing the stated percentages would buy. */
  reliability: number
  /** Distinct probability levels this forecaster's ordering supports. One means none. */
  levels: number
}

export interface ForecastWeight {
  estimate: number
  standardError: number
  low: number
  high: number
  /** Whether the interval excludes zero — this forecast carries information the other does not. */
  informative: boolean
}

/**
 * Does our number add anything the price does not already contain?
 *
 * The strongest question on the page, and a different one from who scores better: a model can lose
 * overall and still hold information the market lacks. Null until the sample can carry a three-term
 * regression, which is deliberate — it converges happily on twenty rows and reports intervals
 * several units wide, which looks like evidence and is not.
 */
export interface EncompassingTest {
  modelWeight: ForecastWeight
  marketWeight: ForecastWeight
  observations: number
  clusters: number
}

/** Where the two disagreed about the favourite, which is the only place accuracy separates them. */
export interface BenchmarkAgreement {
  markets: number
  sameFavourite: number
  disagreements: number
  modelCorrect: number
  marketCorrect: number
}

/** The headline recomputed under the other de-vig, so a verdict cannot rest on one assumption. */
export interface DevigSensitivity {
  method: string
  brierDifference: number
}

export interface BenchmarkSlice {
  scope: 'maps' | 'series'
  fixtures: number
  model: ForecasterScores
  market: ForecasterScores
  brier: PairedDifference | null
  logLoss: PairedDifference | null
  /** 1 − ours/theirs. The share of the market's error we remove; negative means we add error. */
  brierSkillScore: number | null
  modelMurphy: MurphyTerms | null
  marketMurphy: MurphyTerms | null
  agreement: BenchmarkAgreement
  encompassing: EncompassingTest | null
  devigSensitivity: DevigSensitivity[]
}

/** One market of one fixture: what we said, what the book said, and what happened. */
export interface BenchmarkRow {
  fixtureId: number
  startTime: number
  teamA: string
  teamB: string
  market: string
  modelVersion: string
  pricedAt: number
  /** Ours for team A, as it stood when the price was struck — never the fixture's current column. */
  modelProbability: number
  /** The book's for team A, margin removed. */
  marketProbability: number
  oddsA: number
  oddsB: number
  overround: number
  teamAWon: boolean
  modelBrier: number
  marketBrier: number
}

export interface ExplainPlayer {
  accountId: number
  name: string | null
  elo: number
  games: number
  marginFinal: number
  lastSeen: string | null
}

export interface ExplainTeam {
  team: string
  meanElo: number
  weakestElo: number
  eloSpread: number
  players: ExplainPlayer[]
}

export interface ExplainDriver {
  label: string
  what: string
  difference: number
  unit: string
  favours: 'teamA' | 'teamB' | 'neither'
}

export interface Explanation {
  teamAName: string
  teamBName: string
  available: boolean
  reason?: string
  probabilityTeamA?: number | null
  rosterSource?: { teamA: string; teamB: string; confidence: string | null }
  teams?: ExplainTeam[]
  drivers?: ExplainDriver[]
}

/** One team's strength against one other, from the round-robin. */
export interface Pairing {
  teamId: number
  name: string
  /** A single map. The unit the model actually estimates. */
  mapProbability: number
  /** The same pairing over a Bo3. Null only if the conversion could not be made. */
  seriesProbability: number | null
}

export interface RankedTeam {
  rank: number
  teamId: number
  name: string
  logo: string | null
  /** Mean map win probability against every other team in this field. */
  vsField: number
  /** The same, converted per opponent to a Bo3 and then averaged. */
  vsFieldBo3: number
  meanElo: number
  /** How the lineup below was arrived at: "high", "medium" or "low". */
  rosterConfidence: string
  rosterObservedAt: number | null
  lineup: { accountId: number; name: string | null; elo: number }[]
  /** Every other team in the field, hardest first. */
  opponents: Pairing[]
}

/**
 * The model's view of who is strongest in a tournament.
 *
 * Computed on request from current ratings rather than stored, so it is a snapshot rather than a
 * record — there is no result to settle it against and nothing here is ever scored.
 */
export interface Favourites {
  league: { leagueId: number; name: string | null }
  modelVersion: string
  /** Unix seconds: the most recent match folded into the ratings behind this. */
  ratingsAsOf: number
  generatedAt: number
  teams: RankedTeam[]
  /** Teams in the tournament that could not be ranked, and why. */
  excluded: { teamId: number | null; name: string; reason: string }[]
}

// ---------------------------------------------------------------------------------------------
// Fantasy betting.
//
// The one authenticated corner of the site. Everything above this line is anonymous and stays that
// way; a reader who never opens the fantasy tab never sends a token.

export interface Me {
  id: number
  handle: string
  createdAt: string
  /** Configuration, resolved per request — so it can change without signing in again. */
  isSiteAdmin: boolean
}

export interface FantasyLeagueSummary {
  id: number
  slug: string
  name: string
  description?: string | null
  isHouse: boolean
  isPublic?: boolean
  members: number
  /** Present on the caller's own leagues. */
  role?: 'manager' | 'member'
}

export interface SeasonSummary {
  id: number
  slug: string
  name: string
  status: 'draft' | 'open' | 'finished'
  opensAt: string
  closesAt: string
  startingBankroll: number
  includeBots: boolean
  entries: number
  tournaments: number
  joined: boolean
}

/** A fixture the season's window covers but no tournament claims. See the season board. */
export interface UnattributedFixture {
  id: number
  teamAName: string
  teamBName: string
  tournament: string | null
  startTime: string
}

export interface SeasonDetail extends Omit<SeasonSummary, 'entries' | 'tournaments'> {
  markets: string[]
  tournaments: { leagueId: number; name: string | null; tierRank: number }[]
  fixtures: number
  entries: { userId: number; handle: string; isBot: boolean; startingBankroll: number }[]
  role: 'manager' | 'member'
  unattributedFixtures: {
    count: number
    fixtures: UnattributedFixture[]
    note: string | null
  }
}

export interface HouseLineView {
  market: string
  houseLineId: number
  sideAOdds: number
  sideBOdds: number
  /** The model's own number. With no margin taken, the prices above are its reciprocals. */
  sideAProbability: number
  /** Zero: this house takes no cut. Kept on the wire so the arithmetic stays checkable. */
  margin: number
  modelVersion: string | null
  /** Null while the market is open; otherwise why it is not. */
  closed: string | null
  /**
   * Which side team A drew on this map. Null until the lobby says, and null for the series.
   *
   * The tournament decides sides its own way and Valve publishes nothing until the game exists —
   * which is the moment the draft starts. So this arrives with the heroes, not before.
   */
  teamAIsRadiant: boolean | null
  yourBets: { side: 'A' | 'B'; stake: number; decimalOdds: number }[]
  /**
   * Everyone in the league who has taken a side here, biggest stake first.
   *
   * Live, names and all. A leaderboard says who is ahead and nothing about who anyone is playing
   * against; this is the half that makes it a game against the room. `isYou` comes from the server
   * because matching on the handle would break the first time two people had similar names.
   */
  backers: { name: string; isBot: boolean; isYou: boolean; side: 'A' | 'B'; stake: number }[]
}

export interface BoardFixture {
  fixtureId: number
  /** Unix seconds, UTC. Rendered as "in 3 h" — only the browser knows how long that has left. */
  startTime: number
  teamAName: string
  teamBName: string
  /** Valve CDN badge. Null for the ~20% of teams with none — render initials instead. */
  teamALogo: string | null
  teamBLogo: string | null
  seriesType: string
  tournament: string | null
  /** What the line was built from. */
  preDraftProbability: number | null
  /** What the model thinks now the heroes are known — the line deliberately ignores it. */
  draftProbability: number | null
  draftSeenAt: number | null
  score: { a: number; b: number; mapsStarted: number }
  markets: HouseLineView[]
}

/**
 * Two figures that must not be confused.
 *
 * `bankroll` is what ranks the season and counts settled bets only. `available` is what can be
 * staked right now and does deduct open bets. Showing the second on a leaderboard is what made
 * placing a bet look like losing one.
 */
export interface BankrollView {
  starting: number
  bankroll: number
  available: number
  atRisk: number
  returned: number
  bets: number
  open: number
}

export interface Board {
  season: {
    id: number
    name: string
    status: string
    markets: string[]
    opensAt: string
    closesAt: string
    /** The most one bet may risk. A stake above it is reduced to it, not refused. */
    maxStakePerBet: number
    /** The most that may be open on one match at once, counting every market together. */
    maxStakePerFixture: number
  }
  entered: boolean
  bankroll: BankrollView | null
  fixtures: BoardFixture[]
}

export interface MyBet {
  id: number
  market: string
  side: 'A' | 'B'
  stake: number
  decimalOdds: number
  houseProbability: number
  status: 'open' | 'won' | 'lost' | 'void'
  returned: number | null
  voidReason: string | null
  placedAt: number
  settledAt: number | null
  fixture: {
    fixtureId: number
    teamAName: string
    teamBName: string
    startTime: number
    tournament: string | null
    /** Once true the bet is locked: no changing it, no taking it back. */
    started: boolean
  }
}

/**
 * The reader's record against one other member.
 *
 * Only markets where both bet and took opposite sides, and only once settled. Two people on the
 * same team both won or both lost, which says nothing about either of them.
 */
export interface HeadToHead {
  userId: number
  name: string
  isBot: boolean
  won: number
  lost: number
}

export interface LeaderboardRow {
  rank: number
  userId: number
  name: string
  isBot: boolean
  /** Whether this row is the reader. Resolved on the server — see the note on `backers`. */
  isYou: boolean
  strategy: string | null
  starting: number
  /** Settled results only. Open stakes are reported separately, never deducted from this. */
  bankroll: number
  profit: number
  staked: number
  /** Staked on bets still running. */
  atRisk: number
  returned: number
  /** Mandatory: Market Man bets far less than the others and would otherwise read as broken. */
  bets: number
  open: number
}

/**
 * One thing somebody in the league did, recently enough to still be news.
 *
 * The server sends facts and this side writes the sentence, because only the browser knows the
 * reader's timezone and how much width it has. A settled bet arrives as TWO events rather than one
 * that was amended — taking the favourite and being wrong about it are separate things to have
 * happened, and collapsing them would erase every open position in the league.
 */
export interface ActivityEvent {
  at: number
  kind: 'placed' | 'won' | 'lost' | 'void'
  name: string
  isBot: boolean
  /** Resolved on the server — see the note on `backers`. */
  isYou: boolean
  teamAName: string
  teamBName: string
  side: 'A' | 'B'
  market: string
  stake: number
  /**
   * What came back. `null` on a placement and `0` on a loss, and those are not the same nothing:
   * one has not resolved, the other resolved to nothing. `kind` is what tells them apart.
   */
  returned: number | null
}

/**
 * A map that finished, sitting in the feed beside the bets it settled.
 *
 * A separate shape from `ActivityEvent` on purpose: that one is bet-shaped — a name, a stake, a
 * return — and none of it means anything about a map result. Nulling half a record to reuse it is
 * how `null` stops meaning one thing.
 */
export interface ResultEvent {
  /** When the map ended, which is when the thing being reported happened. */
  at: number
  teamAName: string
  teamBName: string
  gameNumber: number
  winner: 'A' | 'B'
  scoreA: number
  scoreB: number
  /** This map ended the series. */
  decided: boolean
}

export interface Leaderboard {
  season: { id: number; name: string; status: string }
  note: string | null
  standings: LeaderboardRow[]
  /** Empty for a reader who has not entered, or has never taken the other side of anybody. */
  yourRecord: HeadToHead[]
  /** Newest first, capped at a dozen. Empty until somebody bets. */
  activity: ActivityEvent[]
  /** Finished maps, newest first. Merged with `activity` by time when rendered. */
  results: ResultEvent[]
}

export interface CareerRow {
  rank: number
  userId: number
  name: string
  isBot: boolean
  profit: number
  seasons: number
  bets: number
}

export interface Career {
  seasons: { id: number; name: string }[]
  standings: CareerRow[]
  note?: string
}

export interface MarketRow {
  userId: number
  name: string
  isBot: boolean
  bets: number
  staked: number
  houseReturned: number
  houseProfit: number
  marketReturned: number
  marketProfit: number
  clvPoints: number | null
  clvBets: number
}

export interface MarketComparison {
  season: { id: number; name: string; status: string }
  coverage: {
    settledBets: number
    comparedBets: number
    fixturesWithAPrice: number
    fixtures: number
  }
  caveats: string[]
  standings: MarketRow[]
}

/**
 * The bearer token for authenticated calls, held here rather than passed to every call site.
 *
 * Set by the auth provider whenever it changes. Null for a signed-out reader, which is the state
 * every anonymous page is in.
 */
let authToken: string | null = null

export function setAuthToken(token: string | null) {
  authToken = token
}

function headers(json: boolean): Record<string, string> {
  const result: Record<string, string> = {}
  if (json) result['Content-Type'] = 'application/json'
  if (authToken !== null) result.Authorization = `Bearer ${authToken}`
  return result
}

/**
 * The server's own message, when it sent one.
 *
 * Every refusal on the fantasy surface carries `{ error }` in words meant for the person reading —
 * "that price is no longer on the board", "that is more than you have". Replacing those with "400
 * Bad Request" would throw away the only part of the response that helps.
 */
async function failure(response: Response, path: string): Promise<Error> {
  try {
    const body = (await response.json()) as { error?: string }
    if (typeof body.error === 'string' && body.error.length > 0) {
      return new Error(body.error)
    }
  } catch {
    // Not JSON, or empty. Fall through to the status line.
  }

  return new Error(`${path} failed: ${response.status} ${response.statusText}`)
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: headers(false) })
  if (!response.ok) {
    throw await failure(response, path)
  }
  return response.json() as Promise<T>
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: headers(body !== undefined),
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    throw await failure(response, path)
  }

  // 204, which every successful PATCH and DELETE here answers with.
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}

/**
 * The minimum edge is never sent.
 *
 * The server's default is the single definition of the bar, and a browser that named a number
 * here would be a second copy of the betting rule — the split that once let a card advise 1.31
 * while printing 1.36 beside it.
 */
const query = (params: URLSearchParams) => {
  const suffix = params.toString()
  return suffix ? `?${suffix}` : ''
}

export const api = {
  schedule: () =>
    get<{ series: SeriesView[]; generatedAt: string }>('/api/matches'),
  upcoming: (leagueId?: number) => {
    const params = new URLSearchParams()
    if (leagueId) params.set('leagueId', String(leagueId))
    return get<Fixture[]>(`/api/upcoming${query(params)}`)
  },
  leagues: (q?: string) =>
    get<LeagueSummary[]>(`/api/leagues${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  leagueHub: (leagueId: number) => get<LeagueHub>(`/api/leagues/${leagueId}`),
  favourites: (leagueId: number) => get<Favourites>(`/api/leagues/${leagueId}/favourites`),
  bets: (leagueId?: number, bookmaker?: string, draftAware?: boolean) => {
    const params = new URLSearchParams()
    if (leagueId) params.set('leagueId', String(leagueId))
    if (bookmaker) params.set('bookmaker', bookmaker)
    // Only sent when on. Absent means pre-draft, which is the server's default and the honest
    // record — post-draft is the counterfactual view and has to be asked for.
    if (draftAware) params.set('draftAware', 'true')
    return get<BetRecord>(`/api/bets${query(params)}`)
  },
  explain: (fixtureId: number) => get<Explanation>(`/api/fixtures/${fixtureId}/explain`),
  model: () => get<ModelCard>('/api/model'),
  stats: (leagueId?: number) =>
    get<Stats>(`/api/stats${leagueId ? `?leagueId=${leagueId}` : ''}`),
  benchmark: (leagueId?: number, bookmaker?: string, point?: 'pre_draft' | 'post_draft') => {
    const params = new URLSearchParams()
    if (leagueId) params.set('leagueId', String(leagueId))
    if (bookmaker) params.set('bookmaker', bookmaker)
    // Only sent when asking for the counterfactual. Absent means pre-draft, which is the server's
    // default and the honest comparison — the flattering one has to be asked for by name.
    if (point === 'post_draft') params.set('point', point)
    return get<MarketBenchmark>(`/api/benchmark${query(params)}`)
  },
  health: () => get<{ status: string; modelVersion: string; matches: number }>('/api/health'),

  // --- Fantasy betting -------------------------------------------------------------------------

  registration: () => get<{ signupOpen: boolean }>('/api/auth/registration'),
  signup: (handle: string, password: string) =>
    send<{ token: string; handle: string }>('POST', '/api/auth/signup', { handle, password }),
  login: (handle: string, password: string) =>
    send<{ token: string; handle: string }>('POST', '/api/auth/login', { handle, password }),
  resetPassword: (token: string, password: string) =>
    send<{ token: string; handle: string }>('POST', '/api/auth/reset', { token, password }),
  me: () => get<Me>('/api/me'),

  fantasyDirectory: () => get<FantasyLeagueSummary[]>('/api/fantasy/leagues'),
  myFantasyLeagues: () => get<FantasyLeagueSummary[]>('/api/fantasy/leagues/mine'),
  createFantasyLeague: (name: string, description: string | null, isPublic: boolean) =>
    send<{ id: number; slug: string; name: string; role: string }>(
      'POST', '/api/fantasy/leagues', { name, description, isPublic }),
  joinFantasyLeague: (token: string) =>
    send<{ id: number; slug: string; name: string; alreadyAMember: boolean }>(
      'POST', '/api/fantasy/leagues/join', { token }),
  createInvite: (leagueId: number, maxUses: number | null, expiresInDays: number | null) =>
    send<{ id: number; token: string; expiresAt: string | null; maxUses: number | null }>(
      'POST', `/api/fantasy/leagues/${leagueId}/invites`, { maxUses, expiresInDays }),

  seasons: (leagueId: number) =>
    get<SeasonSummary[]>(`/api/fantasy/leagues/${leagueId}/seasons`),
  season: (leagueId: number, seasonId: number) =>
    get<SeasonDetail>(`/api/fantasy/leagues/${leagueId}/seasons/${seasonId}`),
  createSeason: (leagueId: number, body: {
    name: string
    opensAt: string
    closesAt: string
    leagueIds: number[]
    markets?: string[]
  }) => send<{ id: number; slug: string }>('POST', `/api/fantasy/leagues/${leagueId}/seasons`, body),
  updateSeason: (leagueId: number, seasonId: number, body: Record<string, unknown>) =>
    send<void>('PATCH', `/api/fantasy/leagues/${leagueId}/seasons/${seasonId}`, body),
  joinSeason: (leagueId: number, seasonId: number) =>
    send<{ id: number; startingBankroll: number; alreadyEntered: boolean }>(
      'POST', `/api/fantasy/leagues/${leagueId}/seasons/${seasonId}/join`),

  board: (leagueId: number, seasonId: number) =>
    get<Board>(`/api/fantasy/leagues/${leagueId}/seasons/${seasonId}/board`),
  placeBet: (leagueId: number, seasonId: number, body: {
    fixtureId: number
    market: string
    side: 'A' | 'B'
    stake: number
    houseLineId: number
  }) => send<{ id: number; stake: number; decimalOdds: number; toReturn: number; bankroll: number }>(
    'POST', `/api/fantasy/leagues/${leagueId}/seasons/${seasonId}/bets`, body),
  restake: (leagueId: number, seasonId: number, betId: number, stake: number) =>
    send<{ id: number; stake: number; decimalOdds: number; toReturn: number; bankroll: number }>(
      'PATCH', `/api/fantasy/leagues/${leagueId}/seasons/${seasonId}/bets/${betId}`, { stake }),
  cancelBet: (leagueId: number, seasonId: number, betId: number) =>
    send<{ cancelled: boolean; bankroll: number }>(
      'DELETE', `/api/fantasy/leagues/${leagueId}/seasons/${seasonId}/bets/${betId}`),
  myBets: (leagueId: number, seasonId: number) =>
    get<{ entered: boolean; bankroll: BankrollView | null; bets: MyBet[] }>(
      `/api/fantasy/leagues/${leagueId}/seasons/${seasonId}/me/bets`),

  leaderboard: (leagueId: number, seasonId: number) =>
    get<Leaderboard>(`/api/fantasy/leagues/${leagueId}/seasons/${seasonId}/leaderboard`),
  marketComparison: (leagueId: number, seasonId: number) =>
    get<MarketComparison>(`/api/fantasy/leagues/${leagueId}/seasons/${seasonId}/market`),
  career: (leagueId: number) => get<Career>(`/api/fantasy/leagues/${leagueId}/career`),
}
